import * as Sentry from '@sentry/react';
import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { isEqual } from 'lodash';

// CONSTANTS
import { AGREEMENT_ACTIONS } from 'constants/agreements';
import { DEFAULT_PER_PAGE, DEFAULT_OFFSET } from 'constants/table';

import {
  receivedAddDamageFail,
  receivedAddDamagePictureFail,
  receivedAgreementFail,
  receivedAgreementHTML,
  receivedAgreementManualNotifiedFail,
  receivedAgreementManualNotifiedSuccess,
  receivedAgreementNotificationFail,
  receivedAgreementNotificationSuccess,
  receivedAgreementsFail,
  receivedAgreementsOffset,
  receivedAgreementsSuccess,
  receivedAgreementSuccess,
  receivedChargeAgreementCustomerFail,
  receivedChargeAgreementCustomerSuccess,
  receivedMarkAsPaidAgreementCustomerSuccess,
  receivedClearAgreement,
  receivedClearFilters,
  receivedClearNotifications,
  receivedContactlessAgreementFail,
  receivedContactlessAgreementSuccess,
  receivedCreateAgreementFail,
  receivedCreateAgreementSuccess,
  receivedCreateAgreementChargeFail,
  receivedCreateAgreementChargeSuccess,
  receivedDeleteAgreementChargesFail,
  receivedDeleteAgreementChargeSuccess,
  receivedDeleteAgreementChargesSuccess,
  receivedDeleteAgreementFail,
  receivedDeleteAgreementSuccess,
  receivedNewFilter,
  receivedPerPageValue,
  receivedPreviewChargeCalculationsSuccess,
  receivedPreviewChargeCalculationsFail,
  receivedRefreshFieldFail,
  receivedRefreshFieldSuccess,
  receivedRefreshFuelSuccess,
  receivedRefreshOdometerSuccess,
  receivedRemoveDamageFail,
  receivedRemoveDamagePictureFail,
  receivedRemoveFilter,
  receivedResetAgreements,
  receivedSelectedView,
  receivedSetAgreement,
  receivedSetColumns,
  receivedSetSearchQuery,
  receivedSetVehicle,
  receivedUpdateAgreementChargesFail,
  receivedUpdateAgreementChargesSuccess,
  receivedUpdateAgreementSuccess,
  receivedUpdateCCFail,
  receivedUpdateCCSuccess,
  receivedUpdateDamageFail,
  receivedUpdateSignatureFail,
  receivedUpdateSignatureSuccess,
  sendCopyFail,
  sendCopySuccess,
  sendForSignatureFail,
  sendForSignatureSuccess,
  startLoading,
  startAgreementsRequest,
  receivedMarkAsPaidAgreementCustomerFail,
  receivedUpdateAgreementChargeSuccess,
  receivedUpdateAgreementChargeFail,
  receivedGeofenceAlertsSuccess,
  receivedGeofenceAlertsFail,
  receivedAgreementVehicleSuccess,
  receivedAgreementVehicleFail,
  receivedRefreshBatterySuccess,
} from './actions';

import {
  charge,
  create,
  createCC,
  contactlessAgreementGet,
  customerGetAgreementHTML,
  customerSendCopy,
  customerSign,
  destroy,
  find,
  markManuallyNotified,
  patch,
  query,
  requestDeleteAgreementCharges,
  requestDownloadAgreement,
  requestEmailAgreement,
  requestGetAgreementHTML,
  requestPreviewChargeCalculations,
  requestUpdateAgreementCharges,
  requestUpdateAgreementCharge,
  sendAgreementForSignature,
  sendCopy,
  sendCustomerNotification,
  sign,
  requestCreateAgreementCharge,
  requestDeleteAgreementCharge,
  geofenceAlerts,
} from './resources';

import {
  createDamage as createVehicleDamage,
  deleteDamage as deleteVehicleDamage,
  createPicture as createVehiclePicture,
  deletePicture as deleteVehiclePicture,
  updateDamage as updateVehicleDamage,
  querySingleVehicle,
} from 'services/Vehicles/resources';

import {
  getVehicle,
  refreshFuel as refreshVehicleFuel,
  refreshOdometer as refreshVehicleOdometer,
  refreshBatteryLevel as refreshVehicleBatteryLevel,
} from 'services/Vehicles/thunks';

import { RootState } from 'store';
import {
  Agreement,
  AgreementCharge,
  ChargeCreation,
  ChargeProviders,
  PaymentMethods,
  PreviewCalculation,
  Recipient,
  SignatureType,
} from 'services/resources';
import { nextRequestId } from 'services/latestRequest';

import { sanitizeNewAgreement, sanitizeAgreementCharges, sanitizeAgreementCharge } from './utils';
import { calculateAutofilledCharges } from 'views/Agreement/utils';

/**
 * Selects a new active view
 */
export const selectView = (view) => (dispatch) => dispatch(receivedSelectedView(view));

/**
 * Adds a new filter to the store
 */
export const addNewFilter = (filter) => (dispatch) => dispatch(receivedNewFilter(filter));

/**
 * Removes a filter from the store
 */
export const removeFilter = (index) => (dispatch) => dispatch(receivedRemoveFilter(index));

/*
 * Clears any modifications to the filters
 */
export const clearFilters = () => (dispatch) => dispatch(receivedClearFilters());

/**
 * Sets the agreement
 */
export const setAgreement = (agreement) => (dispatch) => {
  dispatch(receivedSetAgreement(agreement));

  return Promise.resolve(agreement);
};

/**
 * Clears the agreement from redux store
 */
export const clearAgreement = () => (dispatch) => {
  return dispatch(receivedClearAgreement());
};

/**
 * Sets the vehicle for the agreement
 */
export const setVehicle = (vehicle) => (dispatch) => {
  dispatch(receivedSetVehicle(vehicle));
  return Promise.resolve();
};

/**
 * Sets the columns to be displayed in the table
 */
export const setColumns = (columns) => (dispatch) => dispatch(receivedSetColumns(columns));

export const setSearchQuery = (searchQuery) => (dispatch) => dispatch(receivedSetSearchQuery(searchQuery));

/**
 * Resets the agreements content. Used in cases where the sort is applied for example
 * but you want to stay on the same page with the same per page amount
 */
export const resetAgreements = () => (dispatch) => dispatch(receivedResetAgreements());

/**
 * This in turn sets the active agreements page. Used for when the
 * data for that page has already been loaded
 */
export const setAgreementsOffset = (offset) => (dispatch) => dispatch(receivedAgreementsOffset(offset));

/**
 * Gets a list of agreements
 */
export const getAgreements =
  (
    limit = DEFAULT_PER_PAGE,
    offset = DEFAULT_OFFSET,
    filters = new URLSearchParams(),
    searchQuery = '',
    silent = false
  ) =>
  (dispatch) => {
    const requestId = nextRequestId();

    // A silent request never claims the list, so its response is dropped as
    // superseded and leaves state untouched — callers read the resolved value.
    if (!silent) {
      dispatch(startAgreementsRequest(requestId));
    }

    return query(limit, offset, filters, searchQuery)
      .then((response) => {
        dispatch(receivedAgreementsSuccess(response, offset, requestId));
        return response;
      })
      .catch((response) => {
        dispatch(receivedAgreementsFail(response, requestId));
        throw new Error('Problem fetching agreements');
      });
  };

const _createAgreement = (agreement: Partial<Agreement>, actingAs: number | null, dispatch) => {
  const sanitizedAgreement = sanitizeNewAgreement(agreement);

  return create(sanitizedAgreement, actingAs)
    .then((response) => {
      dispatch(receivedCreateAgreementSuccess(response));
      return Promise.resolve(response);
    })
    .catch((response) => {
      dispatch(receivedCreateAgreementFail(response));

      return Promise.reject(response);
    });
};

export const createAgreement = (agreement: Partial<Agreement>, actingAs: number | null) => (dispatch) => {
  dispatch(startLoading());

  return _createAgreement(agreement, actingAs, dispatch);
};

export const getAgreementContactless = (id: number, contactlessAuthToken: string) => (dispatch) => {
  dispatch(startLoading());
  const token = contactlessAuthToken;

  return contactlessAgreementGet(id, token)
    .then((response) => {
      dispatch(receivedContactlessAgreementSuccess(response));
      return response;
    })
    .catch((error) => {
      dispatch(receivedContactlessAgreementFail(error));
      throw error;
    });
};

export const getAgreementVehicle = (id: number) => (dispatch) => {
  dispatch(startLoading());

  return querySingleVehicle(id)
    .then((response) => {
      dispatch(receivedAgreementVehicleSuccess(response));
      return response;
    })
    .catch((error) => {
      dispatch(receivedAgreementVehicleFail(error));
      throw error;
    });
};

export const getAgreement =
  (id: number): ThunkAction<Promise<Agreement>, RootState, unknown, AnyAction> =>
  (dispatch) => {
    dispatch(startLoading());

    return find(id)
      .then((response) => {
        dispatch(receivedAgreementSuccess(response));
        return response;
      })
      .catch((error) => {
        dispatch(receivedAgreementFail(error));
        throw error;
      });
  };

export const patchAgreement =
  (
    id: number,
    agreement: Partial<Agreement>,
    options: {
      silent?: boolean;
      action?: AGREEMENT_ACTIONS;
    } = {}
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  (dispatch) => {
    if (!options.silent) {
      dispatch(startLoading());
    }

    return patch(id, agreement)
      .then((response) => {
        dispatch(
          receivedUpdateAgreementSuccess(
            {
              agreement: response,
              action: options.action,
            },
            options.silent || false
          )
        );
      })
      .catch((error) => {
        dispatch(receivedAgreementFail(error));
        throw error;
      });
  };

export const deleteAgreement = (id: number) => (dispatch) =>
  destroy(id)
    .then((response) => dispatch(receivedDeleteAgreementSuccess(response)))
    .catch((response) => {
      dispatch(receivedDeleteAgreementFail(response));
      return Promise.reject(response);
    });

export const updateAgreementCharges = (agreementId: number, charges: AgreementCharge[]) => (dispatch) => {
  dispatch(startLoading());

  const sanitizedCharges = sanitizeAgreementCharges(charges);

  return requestUpdateAgreementCharges(agreementId, sanitizedCharges)
    .then((response) => {
      dispatch(receivedUpdateAgreementChargesSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedUpdateAgreementChargesFail(response));
    });
};

export const updateAgreementCharge = (agreementId: number, charge: AgreementCharge) => (dispatch) => {
  dispatch(startLoading());

  const sanitizedCharge = sanitizeAgreementCharge(charge);

  return requestUpdateAgreementCharge(agreementId, sanitizedCharge)
    .then((response) => {
      dispatch(receivedUpdateAgreementChargeSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedUpdateAgreementChargeFail(response));
    });
};

export const deleteAgreementCharges = (agreementId: number, charges: AgreementCharge[]) => (dispatch) => {
  dispatch(startLoading());

  const chargeIds = charges
    .map((agreementCharge) => agreementCharge.id)
    .filter((chargeId) => typeof chargeId === 'number');

  return requestDeleteAgreementCharges(agreementId, chargeIds)
    .then((response) => {
      dispatch(receivedDeleteAgreementChargesSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedDeleteAgreementChargesFail(response));
    });
};

export const previewChargeCalculations =
  (
    charges: AgreementCharge[],
    agreementId?: number
  ): ThunkAction<Promise<PreviewCalculation>, RootState, unknown, AnyAction> =>
  (dispatch) => {
    const sanitizedCharges = sanitizeAgreementCharges(charges);

    return requestPreviewChargeCalculations(sanitizedCharges, agreementId)
      .then((response) => {
        dispatch(receivedPreviewChargeCalculationsSuccess(response));
        return response;
      })
      .catch((response) => {
        dispatch(receivedPreviewChargeCalculationsFail(response));
        return response;
      });
  };

/**
 * Changes the per page value
 */
export const changePerPageValue = (perPage) => (dispatch) => dispatch(receivedPerPageValue(perPage));

export const emailAgreement = (agreementId) => () => requestEmailAgreement(agreementId).then().catch();

export const downloadAgreement = (agreement) => () => {
  const { userAgent } = window.navigator;
  let newTab = null;

  if (userAgent.match('Chrome')) {
    newTab = window.open('', '_blank');
  }

  let autofilledCharges = calculateAutofilledCharges(agreement);

  if (autofilledCharges && isEqual(autofilledCharges, agreement.get('charges'))) {
    autofilledCharges = null;
  }

  return requestDownloadAgreement(agreement.get('id'), autofilledCharges)
    .then((payload) => {
      if (userAgent.match('CriOS')) {
        // Chrome iOS
        const reader = new FileReader();
        reader.onloadend = function () {
          if (typeof reader.result === 'string') {
            window.open(reader.result);
          }
        };
        reader.readAsDataURL(payload);
      } else if (userAgent.match('Chrome')) {
        if (newTab) {
          const url = window.URL.createObjectURL(payload);
          newTab.location.href = url;
        }
      } else {
        const url = window.URL.createObjectURL(payload);

        window.location.href = url;
      }
    })
    .catch();
};

export const getAgreementHTMLContactless = (id: number, contactlessAuthToken: string) => (dispatch) => {
  const token = contactlessAuthToken;

  Sentry.addBreadcrumb({
    category: 'auth',
    message: `Customer token available? ${String(Boolean(token))}`,
    level: 'info',
  });

  return customerGetAgreementHTML(id, token)
    .then((response) => {
      dispatch(receivedAgreementHTML(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedContactlessAgreementFail(response));
      throw response;
    });
};

export const getAgreementHTML =
  (id: number, signatureType: SignatureType = SignatureType.primary) =>
  (dispatch) => {
    return requestGetAgreementHTML(id, signatureType)
      .then((response) => {
        dispatch(receivedAgreementHTML(response));
        return response;
      })
      .catch((response) => {
        dispatch(receivedAgreementFail(response));
        throw response;
      });
  };

export const updateCustomerSignature =
  (agreementId: number, customerSignature: string, signatureType: SignatureType = SignatureType.primary) =>
  (dispatch) => {
    return sign(agreementId, { customerSignature, signatureType })
      .then((response) => {
        dispatch(receivedUpdateSignatureSuccess());
        return response;
      })
      .catch((response) => {
        dispatch(receivedUpdateSignatureFail(response));
        throw response;
      });
  };

export const updateCustopmerSignatureContactless =
  (agreementId: number, customerSignature: string, contactlessAuthToken: string) => (dispatch) => {
    const token = contactlessAuthToken;

    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Customer token available? ${String(Boolean(token))}`,
      level: 'info',
    });

    return customerSign(agreementId, { customerSignature }, token)
      .then((response) => {
        dispatch(receivedUpdateSignatureSuccess());
        return response;
      })
      .catch((response) => {
        dispatch(receivedUpdateSignatureFail(response));
        throw response;
      });
  };

export const sendAgreementCopy = (agreementId: number, recipient: Recipient) => (dispatch) => {
  return sendCopy(agreementId, recipient)
    .then((response) => {
      dispatch(sendCopySuccess());
      return response;
    })
    .catch((response) => {
      dispatch(sendCopyFail());
      throw response;
    });
};

export const sendAgreementCopyContactless =
  (agreementId: number, recipient: Recipient, contactlessAuthToken: string) => (dispatch) => {
    const token = contactlessAuthToken;

    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Customer token available? ${String(Boolean(token))}`,
      level: 'info',
    });

    return customerSendCopy(agreementId, recipient, token)
      .then((response) => {
        dispatch(sendCopySuccess());
        return response;
      })
      .catch((response) => {
        dispatch(sendCopyFail());
        throw response;
      });
  };

export const clearNotifications = () => (dispatch) => dispatch(receivedClearNotifications());

export const updateCC =
  (agreementId: number, stripe: Stripe, customerSource: number, cardElement: StripeCardElement) => async (dispatch) => {
    dispatch(startLoading());

    try {
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        throw error;
      }

      await createCC(token.id, customerSource, agreementId, true);

      const updatedAgreement = await dispatch(getAgreement(agreementId));

      dispatch(receivedUpdateCCSuccess(updatedAgreement));

      return updatedAgreement;
    } catch (error) {
      dispatch(receivedUpdateCCFail(error));

      throw error;
    }
  };

type ChargeCustomerReturnValue = ThunkAction<Promise<Agreement>, RootState, unknown, AnyAction>;

export const chargeCustomer =
  (agreementId: number, provider: ChargeProviders): ChargeCustomerReturnValue =>
  (dispatch) => {
    return charge(agreementId, provider)
      .then((response) => {
        if (provider.paymentMethod !== PaymentMethods.STRIPE) {
          dispatch(receivedMarkAsPaidAgreementCustomerSuccess(response));
        } else {
          dispatch(receivedChargeAgreementCustomerSuccess(response));
        }
        return response;
      })
      .catch((response) => {
        if (provider.paymentMethod !== PaymentMethods.STRIPE) {
          dispatch(receivedMarkAsPaidAgreementCustomerFail(response));
        } else {
          dispatch(receivedChargeAgreementCustomerFail(response));
        }

        // When a credit card failure happens, trigger a refresh
        // of the data to potentially get new failed charges.
        dispatch(getAgreement(agreementId));
        return Promise.reject(response);
      });
  };

export const chargeCustomerStripe =
  (agreementId: number): ChargeCustomerReturnValue =>
  (dispatch) => {
    return dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.STRIPE }));
  };

export const chargeCustomerCash =
  (agreementId: number, reference?: string): ChargeCustomerReturnValue =>
  (dispatch) => {
    return reference
      ? dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.CASH, reference }))
      : dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.CASH }));
  };

export const chargeCustomerDebit =
  (agreementId: number, reference?: string): ChargeCustomerReturnValue =>
  (dispatch) => {
    return reference
      ? dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.DEBIT, reference }))
      : dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.DEBIT }));
  };

export const chargeCustomerCredit =
  (agreementId: number, reference?: string): ChargeCustomerReturnValue =>
  (dispatch) => {
    return reference
      ? dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.CREDIT, reference }))
      : dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.CREDIT }));
  };

export const chargeCustomerRO =
  (agreementId: number, reference: string): ChargeCustomerReturnValue =>
  (dispatch) => {
    return dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.RO, reference }));
  };

export const chargeCustomerInternal =
  (agreementId: number, reference?: string): ChargeCustomerReturnValue =>
  (dispatch) => {
    return reference
      ? dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.INTERNAL, reference }))
      : dispatch(chargeCustomer(agreementId, { paymentMethod: PaymentMethods.INTERNAL }));
  };

export const refreshFuelIn = (vehicleId, fuelUnit, agreementId) => async (dispatch) => {
  await dispatch(refreshVehicleFuel(vehicleId, fuelUnit));

  try {
    const response = await patch(agreementId, {
      fuelIn: null,
      vehicleFuelUnit: fuelUnit,
    });
    dispatch(receivedRefreshFieldSuccess(response));
  } catch (error) {
    dispatch(receivedRefreshFieldFail(error));
  }
};

export const refreshFuelOut = (vehicleId, fuelUnit, agreementId) => async (dispatch) => {
  await dispatch(refreshVehicleFuel(vehicleId, fuelUnit));

  try {
    const response = await patch(agreementId, {
      fuelOut: null,
      vehicleFuelUnit: fuelUnit,
    });
    dispatch(receivedRefreshFieldSuccess(response));
  } catch (error) {
    dispatch(receivedRefreshFieldFail(error));
  }
};

export const refreshFuel = (id, fuelUnit) => (dispatch) =>
  dispatch(refreshVehicleFuel(id, fuelUnit)).then((response) => {
    dispatch(receivedRefreshFuelSuccess(response));
    return response;
  });

export const refreshBatteryLevelIn = (vehicleId, agreementId) => async (dispatch) => {
  await dispatch(refreshVehicleBatteryLevel(vehicleId));

  try {
    const response = await patch(agreementId, {
      batteryLevelIn: null,
    });
    dispatch(receivedRefreshFieldSuccess(response));
  } catch (error) {
    dispatch(receivedRefreshFieldFail(error));
  }
};

export const refreshBatteryLevelOut = (vehicleId, agreementId) => async (dispatch) => {
  await dispatch(refreshVehicleBatteryLevel(vehicleId));

  try {
    const response = await patch(agreementId, {
      batteryLevelOut: null,
    });
    dispatch(receivedRefreshFieldSuccess(response));
  } catch (error) {
    dispatch(receivedRefreshFieldFail(error));
  }
};

export const refreshBatteryLevel = (id) => (dispatch) =>
  dispatch(refreshVehicleBatteryLevel(id)).then((response) => {
    dispatch(receivedRefreshBatterySuccess(response));
    return response;
  });

export const refreshOdometerIn = (id, odometerUnit, agreementId) => async (dispatch) => {
  await dispatch(refreshVehicleOdometer(id, odometerUnit));

  try {
    const response = await patch(agreementId, {
      odometerIn: null,
      vehicleOdometerUnit: odometerUnit,
    });
    dispatch(receivedRefreshFieldSuccess(response));
  } catch (error) {
    dispatch(receivedRefreshFieldFail(error));
  }
};

export const refreshOdometerOut = (id, odometerUnit, agreementId) => async (dispatch) => {
  await dispatch(refreshVehicleOdometer(id, odometerUnit));

  try {
    const response = await patch(agreementId, {
      odometerOut: null,
      vehicleOdometerUnit: odometerUnit,
    });
    dispatch(receivedRefreshFieldSuccess(response));
  } catch (error) {
    dispatch(receivedRefreshFieldFail(error));
  }
};

export const refreshOdometer = (id, odometerUnit) => (dispatch) =>
  dispatch(refreshVehicleOdometer(id, odometerUnit)).then((response) => {
    dispatch(receivedRefreshOdometerSuccess(response));
    return response;
  });

export const sendNotification = (agreementId) => (dispatch) => {
  dispatch(startLoading());

  return sendCustomerNotification(agreementId)
    .then((response) => {
      dispatch(receivedAgreementNotificationSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedAgreementNotificationFail(response));
      return Promise.reject(response);
    });
};

export const markManualNotified = (agreementId) => (dispatch) => {
  dispatch(startLoading());

  return markManuallyNotified(agreementId)
    .then((response) => {
      dispatch(receivedAgreementManualNotifiedSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedAgreementManualNotifiedFail(response));
      return Promise.reject(response);
    });
};

export const sendForSignature = (agreementId, recipient) => async (dispatch) => {
  dispatch(startLoading());
  try {
    const response = await sendAgreementForSignature(agreementId, recipient);
    dispatch(sendForSignatureSuccess(response));
  } catch (error) {
    dispatch(sendForSignatureFail());
    throw error;
  }
};

export const addDamagePicture = (agreement: Agreement, damageId: number, pictures) => async (dispatch) => {
  dispatch(startLoading());
  try {
    await createVehiclePicture(agreement.vehicleSource, damageId, pictures);

    dispatch(getAgreement(agreement.id));
  } catch (error) {
    dispatch(receivedAddDamagePictureFail(error));
    throw error;
  }
};

export const addDamage = (agreement: Agreement, damage, pictures) => async (dispatch) => {
  dispatch(startLoading());
  try {
    const damageResponse = await createVehicleDamage(agreement.vehicleSource, damage);
    if (pictures) {
      // @ts-expect-error TS(2339): Property 'pictures' does not exist on type 'unknown'.
      damageResponse.pictures = await createVehiclePicture(agreement.vehicleSource, damageResponse.id, pictures);
    }

    dispatch(getAgreement(agreement.id));
  } catch (error) {
    dispatch(receivedAddDamageFail(error));
    throw error;
  }
};

export const removeDamage = (agreement: Agreement, damageId: number) => async (dispatch) => {
  dispatch(startLoading());
  try {
    await deleteVehicleDamage(agreement.vehicleSource, damageId);

    dispatch(getAgreement(agreement.id));
  } catch (error) {
    dispatch(receivedRemoveDamageFail(error));
    throw error;
  }
};

export const modifyDamage = (agreement: Agreement, damage) => async (dispatch) => {
  dispatch(startLoading());
  try {
    await updateVehicleDamage(agreement.vehicleSource, damage);

    dispatch(getAgreement(agreement.id));
  } catch (error) {
    dispatch(receivedUpdateDamageFail(error));
    throw error;
  }
};

export const removeDamagePicture = (agreement: Agreement, damageId: number, pictureId: number) => async (dispatch) => {
  dispatch(startLoading());
  try {
    await deleteVehiclePicture(agreement.vehicleSource, damageId, pictureId);

    dispatch(getAgreement(agreement.id));
  } catch (error) {
    dispatch(receivedRemoveDamagePictureFail(error));
    throw error;
  }
};

export const addAgreementCharge = (agreementId: number, charge: ChargeCreation) => async (dispatch, getState) => {
  try {
    const addedCharge = await requestCreateAgreementCharge(agreementId, charge);
    addedCharge.agreementId = Number(addedCharge.agreementId);

    await dispatch(receivedCreateAgreementChargeSuccess(addedCharge));

    const chargeList = getState().getIn(['agreements', 'agreement', 'charges']);
    await dispatch(previewChargeCalculations(chargeList));
  } catch (error) {
    await dispatch(receivedCreateAgreementChargeFail(error));
    throw error;
  }
};

export const removeAgreementCharge = (agreementId: number, charge: AgreementCharge) => async (dispatch, getState) => {
  try {
    const chargeId = charge.id;
    await requestDeleteAgreementCharge(agreementId, chargeId);

    await dispatch(receivedDeleteAgreementChargeSuccess(chargeId));

    const chargeList = getState().getIn(['agreements', 'agreement', 'charges']);
    await dispatch(previewChargeCalculations(chargeList));
  } catch (error) {
    await dispatch(receivedCreateAgreementChargeFail(error));
    throw error;
  }
};

export const getGeofenceAlerts = (agreementId: number) => (dispatch) => {
  dispatch(startLoading());

  return geofenceAlerts(agreementId)
    .then((response) => {
      dispatch(receivedGeofenceAlertsSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedGeofenceAlertsFail(response.data));
      return Promise.reject(response);
    });
};
